import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';
import AutoSave from 'stimulus/auto_save';
import 'tom-select/dist/css/tom-select.css';

// Remote option loading (react-select's async mode), as the user types:
// - `remote-url` value: GET <url>?q= returning [{ value, label }] or the
//   typeahead [{ id, text }] shape.
// - `graphql-query` value: POST /graphql (or `remote-url` if set) with
//   { query, variables: { query: <input> } }; `graphql-path` (e.g.
//   "tags.nodes") locates the option nodes ({ id, name }) in the response.
//
// Picks re-dispatch as `select-field-component:change` ({ value, label })
// for composing controllers (search-input-component), which also use the
// reset/take/release API to keep already-picked options out of the menu.
const ABOVE_CLASS = 'ui-input-field--dropdown-above';

export default class extends Controller {
  static targets = ['select', 'clear'];

  static values = {
    remoteUrl: String,
    graphqlQuery: String,
    graphqlPath: String,
    autoSaveUrl: String,
    clearable: { type: Boolean, default: true },
    debounce: { type: Number, default: 500 }
  };

  connect() {
    // Turbo snapshots the page as-is; destroy() restores the original
    // <select> so the cached copy re-enhances cleanly on restore.
    this.handleBeforeCache = () => this.teardown();
    document.addEventListener('turbo:before-cache', this.handleBeforeCache);
    this.autoSave = new AutoSave({
      url: this.autoSaveUrlValue,
      name: this.selectTarget.name,
      debounce: this.debounceValue,
      label: this.identifier,
      element: this.element,
      onSaved: value => this.dispatch('saved', { detail: { value } }),
      onError: message => this.showError(message)
    });
    this.tomSelect = new TomSelect(this.selectTarget, this.settings());

    // A field near the bottom of the page would otherwise show only its first option or two.
    this.tomSelect.on('dropdown_open', (dropdown) => this.placeDropdown(dropdown));
    this.tomSelect.on('dropdown_close', () => this.element.classList.remove(ABOVE_CLASS));

    this.syncClear();
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.handleBeforeCache);
    this.autoSave?.cancel();
    this.teardown();
  }

  clear(event) {
    event.preventDefault();
    this.tomSelect.clear();
    this.tomSelect.setTextboxValue('');
    this.selectTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this.dispatch('change', { detail: { value: '', label: '' } });
    this.syncClear();
  }

  syncClear() {
    if (!this.hasClearTarget) return;

    this.clearTarget.hidden = !this.tomSelect.getValue();
  }

  // --- API for composing controllers ---

  // Clears text + selection without dispatching a change.
  reset() {
    this.tomSelect.clear(true);
    this.tomSelect.setTextboxValue('');
  }

  take(value) {
    this.tomSelect.removeOption(value);
  }

  release(value, label) {
    this.tomSelect.addOption({ value, text: label });
  }


  settings() {
    const settings = {
      maxItems: this.selectTarget.multiple ? null : 1,
      maxOptions: null,
      allowEmptyOption: false,
      placeholder: this.selectTarget.dataset.placeholder,
      onChange: (value) => this.change(value),
    };

    if (this.selectTarget.multiple && this.clearableValue) settings.plugins = ['remove_button'];

    // A required answer the rest of the form branches on can be changed but not taken back,
    // so Backspace must not empty it. The page can still clear it through `clear`.
    if (!this.clearableValue) settings.onDelete = () => false;

    if (this.hasGraphqlQueryValue || this.hasRemoteUrlValue) {
      settings.shouldLoad = (query) => query.trim().length > 0;
      settings.load = this.hasGraphqlQueryValue
        ? (query, callback) => this.loadGraphqlOptions(query, callback)
        : (query, callback) => this.loadOptions(query, callback);
    }

    return settings;
  }

  // Measured against the viewport, so a fixed footer counts as occupied space.
  placeDropdown(dropdown) {
    const control = this.tomSelect.control.getBoundingClientRect();
    const needed = dropdown.offsetHeight;
    const below = window.innerHeight - control.bottom;

    this.element.classList.toggle(ABOVE_CLASS, below < needed && control.top > below);
  }

  change(value) {
    // TomSelect hides the real <select>, so a pick never produces the `change` a form emits.
    this.selectTarget.dispatchEvent(new Event('change', { bubbles: true }));

    if (value) this.clearError();
    this.syncClear();
    if (this.autoSaveUrlValue) this.autoSave.saveNow(this.emptyToBlank(value));

    if (this.selectTarget.multiple) {
      this.dispatch('change', { detail: { value } });
      return;
    }

    const option = this.tomSelect.options[value];
    this.dispatch('change', {
      detail: { value: value || '', label: option ? option.text : '' },
    });
  }

  emptyToBlank(value) {
    return Array.isArray(value) && value.length === 0 ? [''] : value;
  }

  showError(message) {
    this.element.classList.add('ui-input-field--error');
    this.selectTarget.setAttribute('aria-invalid', 'true');

    const hint = this.element.querySelector('.ui-hint');
    if (!hint) return;

    hint.classList.replace('ui-hint--neutral', 'ui-hint--error');
    hint.setAttribute('role', 'alert');

    const slot = hint.querySelector('[data-hint-text]');
    if (slot) slot.textContent = message;
    hint.hidden = false;
  }

  // A server-rendered error otherwise stands until the next submit, over an answer that
  // no longer has anything wrong with it.
  clearError() {
    if (!this.element.classList.contains('ui-input-field--error')) return;

    this.element.classList.remove('ui-input-field--error');
    this.selectTarget.setAttribute('aria-invalid', 'false');

    const hint = this.element.querySelector('.ui-hint');
    if (!hint) return;

    hint.classList.replace('ui-hint--error', 'ui-hint--neutral');
    hint.removeAttribute('role');

    const text = this.element.dataset.inputFieldHint || '';
    const slot = hint.querySelector('[data-hint-text]');
    if (slot) slot.textContent = text;
    hint.hidden = text === '';
  }

  async loadOptions(query, callback) {
    try {
      const url = new URL(this.remoteUrlValue, window.location.origin);
      url.searchParams.set('q', query);
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(response.statusText);
      callback(this.toOptions(await response.json()));
    } catch {
      callback();
    }
  }

  async loadGraphqlOptions(query, callback) {
    try {
      const response = await fetch(this.remoteUrlValue || '/graphql', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]')?.getAttribute('content'),
        },
        body: JSON.stringify({
          query: this.graphqlQueryValue,
          variables: { query },
        }),
      });
      if (!response.ok) throw new Error(response.statusText);
      const { data, errors } = await response.json();
      if (errors || !data) throw new Error('GraphQL error');

      const nodes = this.graphqlPathValue
        .split('.')
        .reduce((node, key) => node?.[key], data);
      callback(this.toOptions(nodes || []));
    } catch {
      callback();
    }
  }

  toOptions(items) {
    return items.map((item) => ({
      value: String(item.value ?? item.id),
      text: item.label ?? item.text ?? item.name,
    }));
  }

  teardown() {
    this.tomSelect?.destroy();
    this.tomSelect = null;
  }
}
